import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

const boundedText = (maximum: number) => z.string().trim().min(1).max(maximum)

export const fieldChangeOrderContinuationPolicyV1Schema = z.enum([
  "Pause Visit",
  "Continue Original Scope",
])

export const fieldChangeOrderProposalStateV1Schema = z.enum([
  "Awaiting Commercial Review",
  "Commercial Draft",
  "Office Review Required",
  "Ready for Customer",
  "Approved",
  "Declined",
  "Changes Requested",
])

export const fieldChangeOrderRequestedItemV1Schema = z.object({
  serviceId: entityIdSchema,
  quantityMillis: z.number().int().positive().max(1_000_000_000),
}).strict()

export const fieldChangeOrderDraftV1Schema = z.object({
  proposalId: entityIdSchema,
  visitId: entityIdSchema,
  visitVersion: z.number().int().positive(),
  planRecordVersion: z.number().int().positive(),
  serviceOrderId: entityIdSchema,
  scopeDelta: boundedText(10_000),
  requestedItems: z.array(fieldChangeOrderRequestedItemV1Schema).min(1).max(25),
  scheduleImpactMinutes: z.number().int().min(-10_080).max(10_080),
  scheduleImpactNote: boundedText(2_000),
  evidenceMediaIds: z.array(entityIdSchema).min(1).max(10),
  capturedAt: z.iso.datetime({ offset: true }),
}).strict().superRefine((proposal, context) => {
  const uniqueServices = new Set(
    proposal.requestedItems.map((item) => item.serviceId),
  )
  if (uniqueServices.size !== proposal.requestedItems.length) {
    context.addIssue({
      code: "custom",
      message: "Field Change Order requested services must be unique.",
      path: ["requestedItems"],
    })
  }
})

export const fieldChangeOrderPolicyProjectionV1Schema = z.object({
  enabled: z.boolean(),
  continuationPolicy: fieldChangeOrderContinuationPolicyV1Schema,
  evidenceRequired: z.literal(true),
}).strict()

export const fieldChangeOrderProposalProjectionV1Schema = z.object({
  proposalId: entityIdSchema,
  state: fieldChangeOrderProposalStateV1Schema,
  continuationPolicy: fieldChangeOrderContinuationPolicyV1Schema,
  changeOrderId: entityIdSchema.nullable(),
  revisionId: entityIdSchema.nullable(),
  extraWorkAuthorized: z.boolean(),
}).strict()

export type FieldChangeOrderDraftV1 = z.infer<
  typeof fieldChangeOrderDraftV1Schema
>
export type FieldChangeOrderContinuationPolicyV1 = z.infer<
  typeof fieldChangeOrderContinuationPolicyV1Schema
>
export type FieldChangeOrderProposalProjectionV1 = z.infer<
  typeof fieldChangeOrderProposalProjectionV1Schema
>
